/**
 * runPendingSavePlans — the live-fix save path that consumes a per-channel
 * ChannelReorderPlan and executes it via insert-then-reorder.
 *
 * For each channel WITH a plan in `ctx.plans`:
 *   1. Compute a temp-slot base = max(existing slot in plan) + 1
 *   2. For each plan item kind='new': transferFromGalleryToProduct
 *      (collection target) → linkAssetToChannel(productId, channelId,
 *      asset_id, tempSlot+i). The temp slot guarantees no UNIQUE collision
 *      while we materialise inserts. Captured link_id is bound back to the
 *      item via its `tempKey`.
 *   3. Build final link-id list in PLAN ORDER (existing.linkId or the freshly
 *      bound new.link_id) and call reorderProductImages once.
 *
 * Channels WITHOUT a plan stay on the legacy `shifts`-based path (see
 * runPendingSave.ts → runChannelInsertsAppend + runChannelReorderFinal).
 *
 * AI-reference inserts are unaffected — they have no reorder semantics and
 * still go through `runAiRefInserts` in runPendingSave.ts.
 */
import { transferFromGalleryToProduct } from '@/lib/api/product-images'
import { linkAssetToChannel, reorderProductImages } from '@/lib/api/products'
import type {
  ChannelPlanItem,
  ChannelReorderPlans,
} from './channelReorderPlan'

export interface PlanInsertOutcome {
  ok: boolean
  /** Newly created link_id for the temp-slot insert (when successful). */
  linkId?: string
}

async function appendPlanInsert(
  productId: string,
  channelId: string,
  image: { id: string },
  tempSlot: number,
): Promise<PlanInsertOutcome> {
  try {
    const t = await transferFromGalleryToProduct(image.id, productId, {
      targetType: 'collection',
    })
    if (!t?.success || !t.asset_id) return { ok: false }
    const l = await linkAssetToChannel(productId, channelId, t.asset_id, tempSlot)
    if (!l?.success) return { ok: false }
    return { ok: true, linkId: l.link_id }
  } catch {
    return { ok: false }
  }
}

function maxExistingPlanSlot(plan: ChannelPlanItem[]): number {
  // Plan items are positioned at idx+1 — the highest "existing" slot is the
  // largest index where kind==='existing'. We use plan.length as a safe upper
  // bound for the temp-slot base, which is always > any existing slot.
  return plan.length
}

interface RunPlanForChannelInput {
  productId: string
  channelId: string
  plan: ChannelPlanItem[]
}

interface RunPlanForChannelResult {
  ok: number
  err: number
  /** True if a reorder was attempted for this channel. */
  reordered: boolean
}

async function runPlanForChannel(
  input: RunPlanForChannelInput,
): Promise<RunPlanForChannelResult> {
  const { productId, channelId, plan } = input
  let ok = 0
  let err = 0
  const tempKeyToLinkId = new Map<string, string>()
  const base = maxExistingPlanSlot(plan)
  let i = 0
  for (const item of plan) {
    if (item.kind !== 'new') continue
    const out = await appendPlanInsert(productId, channelId, item.image, base + 1 + i)
    if (out.ok) {
      if (out.linkId) tempKeyToLinkId.set(item.tempKey, out.linkId)
      ok++
    } else {
      err++
    }
    i++
  }
  const finalLinkIds = buildFinalLinkIdsFromPlan(plan, tempKeyToLinkId)
  if (finalLinkIds.length === 0) return { ok, err, reordered: false }
  try {
    await reorderProductImages(productId, finalLinkIds, channelId)
    return { ok, err, reordered: true }
  } catch {
    return { ok, err, reordered: false }
  }
}

function buildFinalLinkIdsFromPlan(
  plan: ChannelPlanItem[],
  tempKeyToLinkId: Map<string, string>,
): string[] {
  const out: string[] = []
  for (const item of plan) {
    if (item.kind === 'existing') {
      out.push(item.linkId)
    } else {
      const id = tempKeyToLinkId.get(item.tempKey)
      if (id) out.push(id)
    }
  }
  return out
}

export interface PlanSaveContext {
  productId: string
  plans: ChannelReorderPlans
}

export interface PlanSaveResult {
  ok: number
  err: number
  /** Channels for which a plan-driven save ran (insert + reorder attempted). */
  handledChannels: Set<string>
}

function planHasExistingItems(plan: ChannelPlanItem[]): boolean {
  return plan.some((it) => it.kind === 'existing')
}

/**
 * Top-level orchestrator: runs `runPlanForChannel` for every channel where the
 * plan was seeded from the live QueryCache (i.e. has at least one `existing`
 * item) — that's the live-fix entry condition. Channels with pure-new plans
 * (no existing baseline) fall through to the legacy shifts-based pipeline,
 * keeping the existing test surface and snapshot-driven flows untouched.
 *
 * Returns the set of channels the plan path took over so the caller can skip
 * them in the legacy pending+shifts run.
 */
export async function runChannelPlans(
  ctx: PlanSaveContext,
): Promise<PlanSaveResult> {
  let ok = 0
  let err = 0
  const handledChannels = new Set<string>()
  for (const [channelId, plan] of ctx.plans.entries()) {
    if (plan.length === 0) continue
    if (!planHasExistingItems(plan)) continue
    handledChannels.add(channelId)
    const r = await runPlanForChannel({ productId: ctx.productId, channelId, plan })
    ok += r.ok
    err += r.err
  }
  return { ok, err, handledChannels }
}
